/**
 * Snap a "HH:mm" time string to the nearest multiple of `stepSeconds`.
 *
 * Used for `TextInputMode.TIME` to enforce a step interval regardless of how
 * the value was entered (typing, mobile wheel picker, desktop spinner) — the
 * native `step` attribute is advisory only and isn't honored by typed input
 * or by the iOS/Android time picker.
 *
 * Returns the input unchanged if it can't be parsed. The result is always a
 * valid time of day on the step grid: rounding is done on a 24-hour clock, so
 * a round-up that would overflow past 23:59 wraps to 00:00 (e.g. 23:53 with a
 * 15-min step → 00:00) rather than landing off-grid.
 */
const MINUTES_PER_DAY = 24 * 60;

export function snapTimeToStep(time: string, stepSeconds: number): string {
    const [h, m] = time.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return time;
    const stepMin = Math.max(1, Math.round(stepSeconds / 60));
    const total = h * 60 + m;
    // Round to the nearest step, then wrap on the 24-hour clock so an overflow
    // past 23:59 becomes 00:00 (which is on every step grid).
    const snapped = (Math.round(total / stepMin) * stepMin) % MINUTES_PER_DAY;
    return toHHmm(snapped);
}

/**
 * Decide how a `TextInputMode.TIME` field should react to an Up/Down arrow.
 *
 * The native `<input type="time">` has independent hour and minute segments,
 * and the browser exposes no caret/segment API, so we can't know up front which
 * one the user is editing. Instead, we let the browser apply its native ±1 move
 * first and pass both the value `before` and `after` here:
 *
 * - If the MINUTE segment changed, the user is on minutes — return the value
 *   with the minute stepped by the full interval instead (segment-local: it
 *   wraps within 0–59 and never carries into the hour, matching native segment
 *   semantics). Off-grid minutes snap toward the press direction.
 * - If the HOUR segment changed (or nothing usable changed), return `null` so
 *   the caller leaves the browser's native result untouched (hour ±1).
 *
 * Returns `null` when there's nothing to upgrade (incomplete value, hour-only
 * change, or unparseable input).
 */
export function stepTimeSegment(
    before: string,
    after: string,
    stepSeconds: number,
    direction: 1 | -1
): string | null {
    const stepMin = Math.max(1, Math.round(stepSeconds / 60));
    const [bh, bm] = before.split(':').map(Number);
    const [ah, am] = after.split(':').map(Number);
    if (![bh, bm, ah, am].every(Number.isFinite)) return null;
    // Only upgrade a minute-segment move; leave hour-segment moves to native.
    if (ah !== bh || am === bm) return null;
    const onGrid = bm % stepMin === 0;
    let minute: number;
    if (!onGrid) {
        minute =
            direction === 1
                ? Math.ceil(bm / stepMin) * stepMin
                : Math.floor(bm / stepMin) * stepMin;
    } else {
        minute = bm + direction * stepMin;
    }
    // Segment-local wrap within the hour (no carry), matching native behavior.
    minute = ((minute % 60) + 60) % 60;
    return `${pad(bh)}:${pad(minute)}`;
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

function toHHmm(totalMinutes: number): string {
    return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
}
